/**
 * The control plane's routes and navigation, registered by existing rather
 * than by being imported: `../routes/index.ts` discovers this file by
 * globbing `src/app/<area>/routes.ts` and reads its `bundle` export. Creating
 * this file is the whole act of registration.
 *
 * Every route requires `control_plane.read` (§17.2): viewing is gated at the
 * route, and the two pages that also write (`TrackingEditor`, `ProfileEditor`)
 * already gate their own mutating controls behind `control_plane.write`
 * internally. `guarded()` in `../routes/compose.ts` wraps every route that
 * declares `requires` in `RequireCapability`, so this file only has to state
 * the requirement once per route rather than re-implement the guard.
 *
 * Catalogue and the profile editor are tenant-scoped, not
 * campaign-scoped; their routes carry `$tenantId` directly (`./paths.ts`), read off
 * `useRouteParams()` below.
 *
 * `CONTROL_NAV` lists the catalogue, its second level (overview and the
 * profile). The profile entry is always shown: it links to the open profile
 * (`PROFILE_EDITOR_PATH`), or to `PROFILE_SELECT_PATH`'s picker while none is open.
 * `campaign/accounts/routes.ts`'s own `Account` screen still links into
 * `TrackingEditor` (its "Editar configuração" button) by click alone:
 * `$accountId` lives in the campaign plane, not this tenant-scoped group.
 * `ProfileList` still links each preset into `ProfileEditor` too
 * (`./paths.ts`), already inside a tenant context.
 *
 * `/c/$campaignId/burn-rate` is retired: `campaign/estimate/routes.ts`'s
 * `CostEstimate` took its path and its per-account table, so this file
 * registers no route and no nav entry for it any more.
 *
 * `ACCOUNTS_PATH` no longer has a route registered here:
 * `owner-decisions-fs-campaign-record-and-accounts-route-paths` (2026-09-20)
 * ruled that the "Contas monitoradas" frame (105:6) materially replaces this
 * plane's cost table at the same URL, so `campaign/accounts/routes.ts` now
 * mounts it -- see that file and its `paths.ts` for why the literal itself
 * still lives in `./paths.ts` unchanged. `accountsPath` below still resolves
 * it for the bare-campaign-root redirect.
 */

import { createElement, type ReactNode } from 'react';
import { Gauge, LayoutTemplate, SlidersHorizontal } from 'lucide-react';
import { Navigate, useNavigate, useParams } from '@tanstack/react-router';

import { EntityPage } from '../../chrome/entity/EntitySwitcher';
import type { NavEntry, RouteBundle, RouteModule } from '../routes/contract';
import { useCampaignOrganisationId } from '../session/orgContext';
import { nt } from '../tenant/i18n/nav';
import {
  accountsPath,
  CAMPAIGN_ROOT_PATH,
  CATALOGUE_PATH,
  PROFILE_EDITOR_PATH,
  PROFILE_SELECT_PATH,
  TRACKING_EDITOR_PATH,
} from './paths';
import { useMonitoringProfiles } from './pages/Catalogue';
import { profileCatalogue } from './pages/profile/messages';
import { Catalogue } from './pages/Catalogue';
import { ProfileEditor } from './pages/ProfileEditor';
import { TrackingEditor } from './pages/TrackingEditor';

const CONTROL_PLANE_READ = 'control_plane.read';

/** Untyped on purpose: this router is composed at runtime from every feature
 *  area's glob-discovered bundle, so there is no generated route tree to type
 *  `useParams` against here. `noUncheckedIndexedAccess` is why the value type
 *  carries `| undefined` -- the path pattern guarantees the segment exists at
 *  match time, TypeScript cannot see that, and the callers below default it. */
function useRouteParams(): Record<string, string | undefined> {
  return useParams({ strict: false }) as Record<string, string | undefined>;
}

/** A bare `/c/$campaignId` -- reachable by anyone bookmarking or sharing
 *  a campaign link with no sub-path -- redirects to accounts
 *  (`campaign/accounts/routes.ts`'s campaign-nav entry), the same way
 *  `/t/$tenantId` resolves for a tenant deep link rather than 404ing. */
function CampaignRootRoute(): ReactNode {
  const { campaignId } = useRouteParams();
  return createElement(Navigate, { to: accountsPath(campaignId ?? ''), replace: true });
}

function TrackingEditorRoute(): ReactNode {
  const { campaignId, accountId } = useRouteParams();
  const tenantId = useCampaignOrganisationId();
  if (tenantId === null) return null;
  return createElement(TrackingEditor, {
    campaignId: campaignId ?? '',
    accountId: accountId ?? '',
    tenantId,
  });
}

function CatalogueRoute(): ReactNode {
  const { tenantId } = useRouteParams();
  if (tenantId === undefined) return null;
  return createElement(Catalogue, { tenantId });
}

function ProfileEditorRoute(): ReactNode {
  const { tenantId, profileId } = useRouteParams();
  const profiles = useMonitoringProfiles(tenantId ?? '');
  const navigate = useNavigate();
  if (tenantId === undefined) return null;
  return createElement(
    EntityPage,
    {
      scope: `profile:${tenantId}`,
      options: profiles.isError
        ? 'failed'
        : profiles.isPending
          ? 'loading'
          : profiles.data.items.map((profile) => ({
              id: profile.id,
              name: profile.name,
              detail: profileCatalogue.plural('usage.accounts.other', profile.usage_count, {
                count: profile.usage_count,
              }),
              imageUrl: null,
            })),
      selectedId: profileId ?? null,
      labels: {
        select: profileCatalogue.t('select.label'),
        title: profileCatalogue.t('select.title'),
        search: profileCatalogue.t('select.search'),
        none: profileCatalogue.t('select.none'),
      },
      onSelect: (id: string) =>
        void navigate({ to: PROFILE_EDITOR_PATH, params: { tenantId, profileId: id } }),
      // Matches `campaign/accounts/routes.ts`'s `AccountRoute` and
      // `data/publication/routes.ts`'s `PublicationRoute`: the switcher lives in the page's own
      // header (`ProfileEditor`'s `EmbeddedSwitcher`), not a second row above it.
      embedded: true,
    },
    profileId === undefined
      ? null
      : // Keyed by `profileId`: `ProfileEditor` seeds its editable copies (name, media, night
        // reduction) into local state once per load, guarded on that state still being `null`
        // (`ProfileEditor.tsx`'s own comment on the seeding effect). Without this key, switching
        // profiles through the switcher reuses the same mounted instance and that guard never
        // re-fires, so the screen keeps showing the previous profile's edits under the new
        // profile's name.
        createElement(ProfileEditor, { key: profileId, tenantId, profileId }),
  );
}

export const CONTROL_ROUTES: RouteModule[] = [
  {
    path: CAMPAIGN_ROOT_PATH,
    element: createElement(CampaignRootRoute),
    requires: CONTROL_PLANE_READ,
    // A redirect, never rendered on screen: the destination it redirects to
    // (`accountsPath`) carries the real kind.
    kind: 'data',
  },
  {
    path: TRACKING_EDITOR_PATH,
    element: createElement(TrackingEditorRoute),
    requires: CONTROL_PLANE_READ,
    kind: 'form',
  },
  {
    path: CATALOGUE_PATH,
    element: createElement(CatalogueRoute),
    requires: CONTROL_PLANE_READ,
    kind: 'data',
  },
  {
    path: PROFILE_SELECT_PATH,
    element: createElement(ProfileEditorRoute),
    requires: CONTROL_PLANE_READ,
    kind: 'form',
  },
  {
    path: PROFILE_EDITOR_PATH,
    element: createElement(ProfileEditorRoute),
    requires: CONTROL_PLANE_READ,
    kind: 'form',
  },
];

/** The label reads "Perfis de coleta", not `Catalogue`'s own page title --
 *  `nt('catalogue')` (`../tenant/i18n/nav.ts`) is the sidebar's word for this
 *  screen, kept apart from the heading the page renders on arrival. */
export const CONTROL_NAV: NavEntry[] = [
  {
    path: CATALOGUE_PATH,
    label: () => nt('catalogue'),
    requires: CONTROL_PLANE_READ,
    icon: LayoutTemplate,
    order: 20,
    kind: 'data',
  },
  {
    path: CATALOGUE_PATH,
    label: () => nt('overview'),
    requires: CONTROL_PLANE_READ,
    parent: CATALOGUE_PATH,
    icon: Gauge,
    order: 21,
    kind: 'data',
  },
  {
    path: PROFILE_SELECT_PATH,
    activeOn: PROFILE_EDITOR_PATH,
    label: () => nt('profile'),
    requires: CONTROL_PLANE_READ,
    parent: CATALOGUE_PATH,
    icon: SlidersHorizontal,
    order: 22,
    kind: 'form',
  },
];

// This plane registers no campaign-nav entry: the tracking editor and the
// profile editor are reached from the rows that name the account and the
// profile, and `campaign/accounts/routes.ts` registers `ACCOUNTS_PATH`'s.

export const bundle: RouteBundle = {
  routes: CONTROL_ROUTES,
  nav: CONTROL_NAV,
};
